import { RecordedObservation } from './RouteObservationMap';
import { useEffect, useRef, useState } from 'react';
import type { JourneyLeg, JourneyRoute } from '../../../core/travel';
import { useTranslation } from '../../lib/useTranslation';
import { selectedLegSegments } from './journeyReviewModel';

// Map code loads only after an explicit detail request; recording is independent.
let mapsLoading: Promise<void> | null = null;
declare global { interface Window { google?: { maps: any }; __bossPilotMapsReady?: () => void } }
function loadMaps(): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (mapsLoading) return mapsLoading;
  const key = (import.meta as unknown as { env: Record<string, string> }).env.VITE_GOOGLE_MAPS_BROWSER_KEY;
  if (!key) return Promise.reject(new Error('MAP_NOT_CONFIGURED'));
  mapsLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const failed = () => { clearTimeout(timeout); mapsLoading = null; script.remove(); delete window.__bossPilotMapsReady; reject(new Error('MAP_UNAVAILABLE')); };
    const timeout = window.setTimeout(failed, 15_000);
    window.__bossPilotMapsReady = () => { clearTimeout(timeout); resolve(); delete window.__bossPilotMapsReady; };
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=__bossPilotMapsReady`;
    script.async = true; script.onerror = failed; document.head.appendChild(script);
  });
  return mapsLoading;
}
export default function RecordedJourneyMap({ route, selected }: { route: JourneyRoute; selected?: JourneyLeg }) {
  const t = useTranslation(); const element = useRef<HTMLDivElement>(null); const map = useRef<any>();
  const overlays = useRef<any[]>([]); const fitted = useRef<JourneyRoute>();
  const [failed, setFailed] = useState(false); const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true; setFailed(false);
    void loadMaps().then(() => {
      if (!alive || !element.current || !window.google) return;
      const maps = window.google.maps;
      if (!map.current) map.current = new maps.Map(element.current, { center: { lat: 46.8, lng: 8.2 }, zoom: 8, mapTypeControl: false, streetViewControl: false, fullscreenControl: true });
      overlays.current.forEach(overlay => overlay.setMap(null)); overlays.current = [];
      const bounds = new maps.LatLngBounds();
      const line = (segment: JourneyRoute['segments'][number], highlighted: boolean) => {
        const path = segment.map(point => ({ lat: point.latitude, lng: point.longitude })); path.forEach(point => bounds.extend(point));
        overlays.current.push(new maps.Polyline({ path, map: map.current, strokeColor: highlighted ? '#c25c22' : '#6557b9', strokeWeight: highlighted ? 6 : 4, strokeOpacity: 0.9 }));
      };
      route.segments.forEach(segment => line(segment, false)); selectedLegSegments(route, selected).forEach(segment => line(segment, true));
      const observed = route.segments.flat().sort((a, b) => a.sequence - b.sequence);
      const first = observed[0]; const last = observed[observed.length - 1];
      for (const [point, title] of [[first, t('journey.firstRecordedPosition')], [last, t(route.nextSequence == null ? 'journey.lastRecordedPosition' : 'journey.latestLoadedPosition')]] as const) {
        if (point) overlays.current.push(new maps.Marker({ position: { lat: point.latitude, lng: point.longitude }, map: map.current, title }));
      }
      (route.stops ?? []).forEach(stop => {
        if (stop.latitude == null || stop.longitude == null) return;
        const position = { lat: stop.latitude, lng: stop.longitude }; bounds.extend(position);
        overlays.current.push(new maps.Marker({ position, map: map.current, label: String(stop.order), title: stop.label || t('journey.stopNumber', { number: stop.order }) }));
      });
      if (!bounds.isEmpty() && fitted.current !== route) { map.current.fitBounds(bounds, 32); fitted.current = route; }
    }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; overlays.current.forEach(overlay => overlay.setMap(null)); overlays.current = []; };
  }, [route, selected, t, attempt]);
  if (failed) {const points=route.segments.flat().sort((a,b)=>a.sequence-b.sequence);return <RecordedObservation route={{status:route.complete?'complete':'incomplete',segments:route.segments,observedStart:points[0],observedEnd:points[points.length-1]}}/>;}
  return <>{failed && <div role="status"><p>{t('journey.mapUnavailable')}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div>}<div ref={element} className="jr-map" role="region" aria-label={t('journey.recordedRoute')} hidden={failed} /></>;
}
